<template>
  <div class="success-rate-chart">
    <div class="chart-header">
      <h3 class="chart-title">成功率分布</h3>
      <a-radio-group v-model="orderType" button-style="solid" size="small" @change="handleTypeChange">
        <a-radio-button value="0">代收</a-radio-button>
        <a-radio-button value="1">代付</a-radio-button>
      </a-radio-group>
    </div>

    <div class="chart-container" ref="chartContainer" :style="{ height: '300px' }">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large" tip="图表加载中..." />
      </div>
      <!-- 无数据状态 -->
      <div v-else-if="!hasData" class="no-data-container">
        <div class="no-data-text">暂无数据</div>
        <!-- <a-empty description="暂无数据" /> -->
      </div>
    </div>
  </div>
</template>

<script>
import { getChartComponent } from '@/utils/chartLoader'

export default {
  name: 'SuccessRateChart',
  props: {
    chartData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      orderType: '0',
      chart: null,
      loading: false,
      Line: null // 动态加载的图表类
    }
  },
  computed: {
    hasData () {
      const data = this.chartData || {}
      const todayData = data.todaySum || []
      const yesterdayData = data.yesterdaySum || []
      return todayData.length > 0 || yesterdayData.length > 0
    }
  },
  watch: {
    chartData: {
      handler (newData) {
        console.log('SuccessRateChart chartData changed:', newData)
        if (newData && Object.keys(newData).length > 0) {
          this.$nextTick(() => {
            this.renderChart()
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    try {
      // 动态导入G2Plot
      console.log('开始加载G2Plot Line for SuccessRateChart...')
      this.Line = await getChartComponent('Line')
      console.log('G2Plot Line for SuccessRateChart加载成功')
      this.$nextTick(() => {
        this.initChart()
      })
    } catch (error) {
      console.error('加载图表组件失败:', error)
      this.$message.error('图表组件加载失败')
    }
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  },
  methods: {
    // 初始化图表
    initChart () {
      if (!this.$refs.chartContainer) {
        console.warn('SuccessRate chart container not found')
        return
      }
      this.renderChart()
    },

    // 渲染图表
    renderChart () {
      try {
        if (!this.$refs.chartContainer || !this.Line) {
          console.warn('SuccessRate chart container not found or G2Plot not loaded, skip rendering')
          return
        }

        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }

        this.loading = true

        const data = this.chartData || {}
        const todayData = data.todaySum || []
        const yesterdayData = data.yesterdaySum || []

        console.log('SuccessRateChart: Rendering chart with data:', { todayData, yesterdayData })

        if (!this.hasData) {
          this.loading = false
          console.log('SuccessRateChart: No data available for chart')
          return
        }

        // 处理数据
        const todayDataMap = new Map(todayData.map(item => [item.hour, item]))
        const yesterdayDataMap = new Map(yesterdayData.map(item => [item.hour, item]))

        const hours = Array.from({ length: 24 }, (_, i) => i + 1)
        const chartData = []

        hours.forEach(hour => {
          const todayItem = todayDataMap.get(hour.toString()) || { hour: hour.toString(), successRate: '0.00', successCount: 0, totalCount: 0 }
          const yesterdayItem = yesterdayDataMap.get(hour.toString()) || { hour: hour.toString(), successRate: '0.00', successCount: 0, totalCount: 0 }

          chartData.push({
            hour: `${hour}`,
            rate: parseFloat(todayItem.successRate),
            type: '今日成功率'
          })
          chartData.push({
            hour: `${hour}`,
            rate: parseFloat(yesterdayItem.successRate),
            type: '昨日成功率'
          })
        })

        // 如果全为0，给一条极小的非零数据，防止G2Plot报错
        if (chartData.every(d => d.rate === 0) && chartData.length > 0) {
          chartData[0].rate = 0.01
        }

        console.log('SuccessRateChart: Chart data processed:', chartData)

        // 确保容器存在且有尺寸
        if (!this.$refs.chartContainer || this.$refs.chartContainer.offsetWidth === 0) {
          console.warn('SuccessRateChart: Chart container has no width, retrying...')
          setTimeout(() => this.renderChart(), 100)
          return
        }

        console.log('SuccessRateChart: Container width:', this.$refs.chartContainer.offsetWidth)

        try {
          this.chart = new this.Line(this.$refs.chartContainer, {
            data: chartData,
            xField: 'hour',
            yField: 'rate',
            seriesField: 'type',
            appendPadding: [10, 10, 10, 10], // 增加内边距，确保坐标轴显示完整
            yAxis: {
              min: 0,
              max: 100,
              nice: true,
              title: {
                text: '成功率(%)',
                style: {
                  fontSize: 12,
                  fill: '#666'
                },
                autoRotate: true,
                position: 'end'
              },
              label: {
                formatter: (v) => `${v}%`,
                style: {
                  fontSize: 11,
                  fill: '#666'
                },
                autoHide: false,
                autoRotate: false
              },
              grid: {
                line: {
                  style: {
                    stroke: '#f0f0f0',
                    lineWidth: 1,
                    lineDash: [4, 4]
                  }
                }
              },
              tickCount: 5, // 设置刻度数量
              tickLine: {
                style: {
                  stroke: '#d9d9d9',
                  lineWidth: 1
                },
                length: 5
              },
              line: {
                style: {
                  stroke: '#d9d9d9',
                  lineWidth: 1
                }
              }
            },
            xAxis: {
              title: {
                text: '小时',
                style: {
                  fontSize: 12,
                  fill: '#666'
                },
                autoRotate: true,
                position: 'end'
              },
              label: {
                style: {
                  fontSize: 11,
                  fill: '#666'
                },
                autoRotate: false,
                formatter: (v) => `${v}时`
              },
              tickLine: {
                style: {
                  stroke: '#d9d9d9',
                  lineWidth: 1
                },
                length: 5
              },
              line: {
                style: {
                  stroke: '#d9d9d9',
                  lineWidth: 1
                }
              },
              tickCount: 12 // 设置刻度数量，避免所有小时都显示导致拥挤
            },
            color: ['#1890ff', '#f5222d'],
            lineStyle: ({ type }) => ({
              lineDash: type === '昨日成功率' ? [4, 4] : undefined,
              opacity: 0.7,
              lineWidth: 2
            }),
            point: {
              size: 3,
              shape: 'circle',
              style: ({ type }) => ({
                fillOpacity: type === '今日成功率' ? 1 : 0,
                stroke: type === '今日成功率' ? '#1890ff' : '#f5222d'
              })
            },
            tooltip: {
              shared: true,
              showMarkers: false,
              formatter: (datum) => {
                return {
                  name: datum.type,
                  value: `${datum.rate.toFixed(2)}%`
                }
              }
            },
            legend: {
              position: 'top-right'
            },
            // 禁用动画，避免path-in动画报错
            animation: false,
            smooth: true,
            connectNulls: true,
            meta: {
              rate: {
                alias: '成功率',
                min: 0,
                max: 100
              }
            },
            annotations: [
              // 添加零线
              {
                type: 'line',
                start: ['min', 0],
                end: ['max', 0],
                style: {
                  stroke: '#d9d9d9',
                  lineWidth: 1
                }
              }
            ]
          })

          this.chart.render()
          this.loading = false
          console.log('SuccessRateChart: Chart rendered successfully')
        } catch (renderError) {
          console.error('首次渲染失败，尝试简化配置:', renderError)
          // 如果首次渲染失败，尝试使用最简配置重新渲染
          if (this.chart) {
            this.chart.destroy()
            this.chart = null
          }

          this.chart = new this.Line(this.$refs.chartContainer, {
            data: chartData,
            xField: 'hour',
            yField: 'rate',
            seriesField: 'type',
            color: ['#1890ff', '#f5222d'],
            animation: false, // 禁用动画
            smooth: false, // 禁用平滑曲线
            point: false, // 禁用点
            legend: {
              position: 'top-right'
            }
          })
          this.chart.render()
          this.loading = false
          console.log('SuccessRateChart: Chart rendered with simplified config')
        }
      } catch (error) {
        this.loading = false
        console.error('Error rendering SuccessRate chart:', error)
        this.$message.error('成功率图表渲染失败')
      }
    },

    // 图表自适应
    resizeChart () {
      if (this.chart) {
        try {
          this.chart.forceFit()
        } catch (error) {
          console.error('Error resizing SuccessRate chart:', error)
        }
      }
    },

    // 处理类型变化
    handleTypeChange () {
      this.$emit('handleSucRateTypeChange', this.orderType)
    }
  }
}
</script>

<style lang="less" scoped>
.success-rate-chart {
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .chart-title {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }
  }

  .chart-container {
    width: 100%;
    position: relative;

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 300px;
    }

    .no-data-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 300px;
    }
  }
}
</style>
